import { useState } from 'react';
import type { Row } from '@tanstack/react-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus } from 'lucide-react';
import {
  EditableTable,
  EditableTableRowActions,
  EditableTableCell,
  type EditableTableRowActionState,
  editableRowClass,
} from '@/components/ui/data-table/editable-table';
import AssignmentCardResourceDialog from '../AssignmentCardResourceDialog';
import AssignmentCardResourceAttachmentDialog from '../AssignmentCardResourceAttachmentDialog';
import {
  type PlannedResource,
  useUpdatePlannedResource,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import type { AssignmentListRowData } from './types';
import { NULL_VALUE } from 'constants/displayConstants';
import { Controller, useForm } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

// resources are frozen while the assignment is pending review
function canModifyPlannedResources(
  row: Row<AssignmentListRowData>,
  canEditCards: boolean
) {
  return (
    canEditCards &&
    row.original.walaId !== null &&
    row.original.actualStatus !== 'awaiting_review' &&
    row.original.actualStatus !== null
  );
}

export function CardResources({
  row,
  canEditCards,
}: {
  row: Row<AssignmentListRowData>;
  canEditCards: boolean;
}) {
  const plannedResources = row.original.plannedResources ?? [];
  const updateWala = useUpdateWorkAssignmentListAttachment();
  const canModifyResources = canModifyPlannedResources(row, canEditCards);
  const [attachmentResource, setAttachmentResource] =
    useState<PlannedResource | null>(null);

  const handleDelete = async (resource: PlannedResource) => {
    if (!row.original.walaId) return;
    await updateWala.mutateAsync({
      walaId: row.original.walaId,
      plannedResources: (row.original.plannedResources ?? []).filter(
        (currentResource) => currentResource.id !== resource.id
      ),
    });
  };

  return (
    <Card className="tw:bg-transparent" size="sm">
      <CardHeader>
        <CardTitle>5. Resources Assigned</CardTitle>
      </CardHeader>
      <CardContent>
        <AssignmentResourceMenu row={row} canEditCards={canEditCards} />
        <EditableTable<PlannedResource>
          data={plannedResources}
          columns={[
            { id: 'resource', header: 'Resource' },
            { id: 'leader', header: 'Leader' },
            { id: 'numberOfPersons', header: '# of Persons' },
            { id: 'contactInfo', header: 'Contact Info' },
            { id: 'reportingInfo', header: 'Reporting Info / Notes' },
            { id: '204a', header: '204a' },
            { id: 'actions', header: '' },
          ]}
          getRowId={(resource) => resource.id}
          canEdit={canModifyResources}
          emptyMessage="No resources assigned"
          renderRow={(resource, actions) => (
            <EditablePlannedResourceRow
              resource={resource}
              canEditCards={canModifyResources}
              actions={actions}
              onShowAttachment={() => setAttachmentResource(resource)}
            />
          )}
          onConfirmDelete={handleDelete}
        />
      </CardContent>
      {attachmentResource && (
        <AssignmentCardResourceAttachmentDialog
          onClose={() => setAttachmentResource(null)}
          data={{ ...attachmentResource, walaId: row.original.walaId }}
        />
      )}
    </Card>
  );
}

function AssignmentResourceMenu({
  row,
  canEditCards,
}: {
  row: Row<AssignmentListRowData>;
  canEditCards: boolean;
}) {
  const [showResourceModal, setShowResourceModal] = useState(false);
  const canAttachResources = canModifyPlannedResources(row, canEditCards);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              size="sm"
              disabled={!canAttachResources}
            />
          }
        >
          <Plus /> Add
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            disabled={!canAttachResources}
            onClick={() => setShowResourceModal(true)}
          >
            + Add resource
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="tw:hidden">
            + Add from roster
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="tw:hidden">
            + Add documentation entry
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {showResourceModal && row.original.walaId && (
        <AssignmentCardResourceDialog
          walaId={row.original.walaId}
          onClose={() => setShowResourceModal(false)}
          plannedResources={row.original.plannedResources}
        />
      )}
    </>
  );
}

type PlannedResourceFormValues = Pick<
  PlannedResource,
  'leader' | 'numberOfPersons' | 'contactInfo' | 'reportingInfo' | 'has204a'
>;

function EditablePlannedResourceRow({
  resource,
  canEditCards,
  actions,
  onShowAttachment,
}: {
  resource: PlannedResource;
  canEditCards: boolean;
  actions: EditableTableRowActionState;
  onShowAttachment: () => void;
}) {
  const { isEditing } = actions;
  const updateResource = useUpdatePlannedResource(resource.id);
  const defaultValues: PlannedResourceFormValues = {
    leader: resource.leader ?? '',
    numberOfPersons: resource.numberOfPersons ?? null,
    contactInfo: resource.contactInfo ?? '',
    reportingInfo: resource.reportingInfo ?? '',
    has204a: resource.has204a ?? false,
  };
  const { register, handleSubmit, reset, control } =
    useForm<PlannedResourceFormValues>({
      defaultValues,
    });

  const handleSave = handleSubmit(async (values) => {
    await updateResource.mutateAsync({ ...resource, ...values });
    actions.finishEdit();
  });

  return (
    <TableRow
      onClick={isEditing ? undefined : actions.startEdit}
      className={editableRowClass(canEditCards, isEditing)}
    >
      <TableCell>{resource.name}</TableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={resource.leader || NULL_VALUE}
      >
        <Input {...register('leader')} />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={resource.numberOfPersons ?? NULL_VALUE}
      >
        <Input
          type="number"
          min={0}
          {...register('numberOfPersons', {
            setValueAs: (v) => (v === '' ? null : Number(v)),
          })}
        />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={resource.contactInfo || NULL_VALUE}
      >
        <Input {...register('contactInfo')} />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={resource.reportingInfo || NULL_VALUE}
      >
        <Input {...register('reportingInfo')} />
      </EditableTableCell>
      <EditableTableCell
        isEditing={isEditing}
        displayValue={<Checkbox checked={resource.has204a ?? false} disabled />}
      >
        <Controller
          control={control}
          name="has204a"
          render={({ field }) => (
            <Checkbox
              checked={field.value ?? false}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </EditableTableCell>
      <TableCell>
        <div className="tw:flex tw:items-center tw:gap-2">
          <EditableTableRowActions
            isEditing={isEditing}
            canEdit={canEditCards}
            onSave={handleSave}
            onCancel={() => {
              reset(defaultValues);
              actions.cancelEdit();
            }}
            onRequestDelete={actions.requestDelete}
          />
          {resource.has204a && !isEditing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onShowAttachment();
              }}
            >
              204a
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
